import {
  Box,
  Chip,
  ClickAwayListener,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import { useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";
import config from "../../../../styles/config";
import { UserApi } from "../../../../api/auth";
import { useTheme } from "@mui/material/styles";
import UpgradePlanCard from "./UpgradePlanCard";
import PerfectScrollbar from "react-perfect-scrollbar";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import { configState, loggedEmployee } from "../../../../recoil/atoms";
import Transitions from "../../../../styles/ui-component/extended/Transitions";
import { useState, useRef, useEffect, MouseEvent, SetStateAction } from "react";
import { IconLogout, IconReplace, IconSettings, IconUser } from "@tabler/icons";
import SwitchBranch from "../../../../pages/Authentication/auth-forms/SwitchBranch";

const ProfileSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [viewBranches, setViewBranches] = useState<boolean>(false);

  const logoutConfig = useRecoilValue(configState);
  const loggedInEmployee = useRecoilValue(loggedEmployee);
  /* 
  anchorRef is used on different components and specifying
  one type leads to other components throwing an error
  */ 
  const anchorRef = useRef<any>(null);

  const handleLogout = async () => {
    localStorage.removeItem("AccessToken");
    const user = new UserApi(logoutConfig);
    user.logout();
    localStorage.clear();     
    navigate("login");
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event?.target)) {
      return;
    }
    setOpen(false);
    setViewBranches(false);
  };

  const handleListItemClick = (
    event: MouseEvent<HTMLDivElement, MouseEvent>,
    index: SetStateAction<number>,
    route = "",
    state?: any
  ) => {
    setSelectedIndex(index);
    handleClose(event);

    if (route && route !== "") {
      navigate(route, { state: state });
    }
  };
  
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen?.current === true && open === false) {
      anchorRef?.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Chip
        sx={{
          height: "48px",
          alignItems: "center",
          borderRadius: "27px",
          transition: "all .2s ease-in-out",
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            "& svg": {
              stroke: theme.palette.primary.light,
            },
          },
          "& .MuiChip-label": {
            lineHeight: 0,
          },
        }}
        icon={
          <UpgradePlanCard
            nameProfile={loggedInEmployee?.User?.FirstName.concat(" ", loggedInEmployee?.User?.FamilyName)}
            sx={{
              ...theme.typography,
              margin: "8px 0 8px 8px !important",
              cursor: "pointer",
            }}
            ref={anchorRef}
            ac={open ? "menu-list-grow" : undefined}
            ah="true"
            color="inherit"
          />
        }
        label={<IconSettings stroke={1.7} size="1.5rem" color={theme.palette.primary.main} />}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Box sx={{ p: 2 }}>
                    <Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">Welcome, </Typography>
                        <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                          {loggedInEmployee?.User?.FirstName} {loggedInEmployee?.User?.FamilyName}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                  <PerfectScrollbar
                    style={{
                      height: "100%",
                      maxHeight: "calc(100vh - 350px)",
                      overflowX: "hidden",
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <List
                        component="nav"
                        sx={{
                          width: "100%",
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: "10px",
                          [theme.breakpoints.down("md")]: {
                            minWidth: "100%",
                          },
                          "& .MuiListItemButton-root": {
                            mt: 2,
                          },
                        }}
                      >
                        <ListItemButton
                          sx={{
                            borderRadius: `${config.borderRadius}px`,
                            bgcolor: theme.palette.primary.light,
                          }}
                          selected={selectedIndex === 1}
                          onClick={(event: any) => handleListItemClick(event, 1, "/profile/edit", loggedInEmployee)}
                        >
                          <ListItemIcon>
                            <IconUser stroke={1.7} size="1.4rem" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Grid container spacing={1} justifyContent="space-between">
                                <Grid item>
                                  <Typography variant="body2">Profile</Typography>
                                </Grid>
                              </Grid>
                            }
                          />
                        </ListItemButton>
                        <>
                          <ListItemButton
                            sx={{ borderRadius: `${config.borderRadius}px`, bgcolor: theme.palette.primary.light }}
                            selected={selectedIndex === 0}
                            onClick={(event: any) => setViewBranches(!viewBranches)}
                          >
                            <ListItemIcon>
                              <IconReplace stroke={1.7} size="1.4rem" />
                            </ListItemIcon>
                            <ListItemText primary={<Typography variant="body2">Switch Account</Typography>} />
                          </ListItemButton>
                          {viewBranches ? <SwitchBranch /> : null}
                        </>
                        <ListItemButton
                          sx={{ borderRadius: `${config.borderRadius}px`, bgcolor: theme.palette.warning.light }}
                          selected={selectedIndex === 4}
                          onClick={handleLogout}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.7} size="1.4rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                        </ListItemButton>
                      </List>
                    </Box>
                  </PerfectScrollbar>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
